import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  Stack,
  alpha,
  Tabs,
  Tab,
  Button,
  useTheme,
} from '@mui/material';
import {
  School,
  LocationOn,
  Verified,
  TrendingUp,
  Groups,
  EmojiEvents,
  Public,
  Star,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { getPublicColleges, getImageUrl } from '../../services/api';
import { scrollToSection } from '../../utils/scroll';

// ---------- Performance Statistics (static data) ----------
const performanceMetrics = [
  {
    icon: <Groups sx={{ fontSize: 40 }} />,
    value: '2,340',
    label: 'Total Students Placed',
    sublabel: 'Across all partner colleges',
    color: '#8a211d',
  },
  {
    icon: <School sx={{ fontSize: 40 }} />,
    value: '15+',
    label: 'Partner Countries',
    sublabel: 'Worldwide presence',
    color: '#2E7D32',
  },
  {
    icon: <EmojiEvents sx={{ fontSize: 40 }} />,
    value: '94%',
    label: 'Average Success Rate',
    sublabel: 'Admission success across programs',
    color: '#2f2522',
  },
  {
    icon: <Star sx={{ fontSize: 40 }} />,
    value: '50+',
    label: 'Partner Institutions',
    sublabel: 'Verified & NMC approved',
    color: '#8B5CF6',
  },
];

// ---------- Success Highlights (static data) ----------
const successHighlights = [
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: '100% NMC/MCI Approved Universities',
  },
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: '5,000+ Successful Placements Since 2009',
  },
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: 'Personalized College Selection & Application Support',
  },
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: 'End-to-End Admission Process Assistance',
  },
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: 'FMGE/NExT Exam Preparation & Coaching',
  },
  {
    icon: <Verified sx={{ color: '#8a211d', fontSize: 28 }} />,
    text: 'Pre-departure Orientation & Visa Guidance',
  },
];

// ---------- Partner Logo Placeholders (static data) ----------
const partnerLogos = Array.from({ length: 12 }, (_, i) => ({
  id: i + 1,
  initials: String.fromCharCode(65 + (i % 26)) + String.fromCharCode(65 + ((i + 1) % 26)),
  name: `Partner University ${i + 1}`,
}));

// ---------- Country flag mapping ----------
const countryFlags = {
  India: '🇮🇳',
  Bangladesh: '🇧🇩',
  Nepal: '🇳🇵',
  Russia: '🇷🇺',
  China: '🇨🇳',
  Ukraine: '🇺🇦',
  Philippines: '🇵🇭',
  Kazakhstan: '🇰🇿',
  Kyrgyzstan: '🇰🇬',
  Uzbekistan: '🇺🇿',
  Georgia: '🇬🇪',
};

// ---------- Highlights generator based on college data ----------
const getCollegeHighlights = (college) => {
  const highlights = [];
  if (college.isTopCollege) highlights.push('Top College');
  if (college.website) highlights.push('NMC Approved');
  if (college.established) highlights.push(`Est. ${college.established}`);
  if (college.ranking > 0) highlights.push(`Rank #${college.ranking}`);
  if (college.state) highlights.push(college.state);
  highlights.push('English Medium');
  return highlights.slice(0, 4);
};

// ---------- College images (fallbacks based on country) ----------
const fallbackImages = {
  Uzbekistan: 'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80',
  Russia: 'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80',
  Bangladesh: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80',
  Nepal: 'https://images.unsplash.com/photo-1590682680695-43b964a3ae17?w=800&q=80',
  Philippines: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=800&q=80',
  Georgia: 'https://images.unsplash.com/photo-1523050854058-8df90110c7f1?w=800&q=80',
  Kyrgyzstan: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80',
  China: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=800&q=80',
  Ukraine: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&q=80',
  Kazakhstan: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80',
};

const defaultImage = 'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80';

const getCollegeImage = (college) => {
  const backendImage = getImageUrl(college.thumbnailImage);
  if (backendImage) return backendImage;
  return fallbackImages[college.country] || defaultImage;
};

// ---------- Tab Panel Component ----------
const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ mt: 4 }}>
    {value === index && children}
  </Box>
);

// ---------- College Card Component ----------
const CollegeCard = ({ college }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        '& .college-image': {
          transform: 'scale(1.08)',
        },
        '& .view-details': {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
      position: 'relative',
    }}
  >
    {/* Image */}
    <Box sx={{ position: 'relative', overflow: 'hidden', height: 200 }}>
      <CardMedia
        component="img"
        className="college-image"
        image={getCollegeImage(college)}
        alt={college.name}
        sx={{
          height: 200,
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(47,37,34,0.95) 0%, transparent 100%)',
          p: 2,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
            {college.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" sx={{ fontSize: '1.2rem' }}>
              {countryFlags[college.country] || ''}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
              {college.country}
            </Typography>
          </Stack>
        </Box>
        {college.ranking > 0 && (
          <Chip
            label={`#${college.ranking}`}
            size="small"
            sx={{
              bgcolor: '#8a211d',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}
          />
        )}
      </Box>
    </Box>

    <CardContent sx={{ flexGrow: 1, p: 3 }}>
      {/* Highlights */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {getCollegeHighlights(college).map((item, i) => (
          <Chip
            key={i}
            label={item}
            size="small"
            sx={{
              bgcolor: alpha('#8a211d', 0.1),
              color: '#8a211d',
              fontWeight: 600,
              fontSize: '0.7rem',
              mb: 0.5,
            }}
          />
        ))}
      </Stack>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2.5, lineHeight: 1.7, flexGrow: 1 }}
      >
        {college.description}
      </Typography>

      {/* Stats */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            City
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
            {college.city || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Ranking
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '0.85rem' }}>
            {college.ranking > 0 ? `#${college.ranking}` : 'Listed'}
          </Typography>
        </Box>
      </Box>

      {/* View Details Button (shown on hover) */}
      <Button
        className="view-details"
        variant="text"
        endIcon={<ArrowForward />}
        onClick={() => scrollToSection('contact')}
        sx={{
          mt: 2,
          fontWeight: 600,
          color: '#8a211d',
          opacity: 0,
          transform: 'translateY(10px)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { bgcolor: alpha('#8a211d', 0.08) },
        }}
      >
        View Details
      </Button>
    </CardContent>
  </Card>
);

// ---------- Constants for Load More ----------
const INITIAL_LOAD = 8;
const LOAD_MORE_COUNT = 8;

// ---------- Main PortfolioSection Component ----------
const PortfolioSection = () => {
  const [category, setCategory] = useState('all');
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD);
  const theme = useTheme();

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const response = await getPublicColleges();
      setColleges(response.data.data);
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derive categories dynamically from college countries with custom order
  const categories = useMemo(() => {
    const countrySet = new Set(colleges.map((c) => c.country));
    const countries = Array.from(countrySet);
    
    // Define preferred order - India first, then Bangladesh
    const preferredOrder = ['India', 'Bangladesh'];
    
    // Sort: preferred countries first (in order), then rest alphabetically
    countries.sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a);
      const bIndex = preferredOrder.indexOf(b);
      
      // Both are in preferred list - sort by preferred order
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      // Only a is in preferred list
      if (aIndex !== -1) return -1;
      // Only b is in preferred list
      if (bIndex !== -1) return 1;
      // Neither is in preferred list - sort alphabetically
      return a.localeCompare(b);
    });
    
    return [
      { id: 'all', label: 'All' },
      ...countries.map((country) => ({
        id: country.toLowerCase(),
        label: country,
      })),
    ];
  }, [colleges]);

  const handleCategoryChange = (event, newValue) => {
    setCategory(newValue);
    setVisibleCount(INITIAL_LOAD);
  };

  const filteredColleges =
    category === 'all'
      ? colleges
      : colleges.filter(
          (college) => college.country.toLowerCase() === category
        );

  const displayColleges = filteredColleges.slice(0, visibleCount);
  const hasMore = visibleCount < filteredColleges.length;
  const remainingCount = filteredColleges.length - visibleCount;

  return (
    <Box id="portfolio" sx={{ scrollMarginTop: 100 }}>
      {/* ---------- Header Banner ---------- */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(138,33,29,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 3,
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 800,
            }}
          >
            Our{' '}
            <Box component="span" sx={{ color: 'white' }}>
              Portfolio
            </Box>
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              opacity: 0.9,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.15rem' },
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Showcasing our network of partner medical colleges and universities across the globe —
            each one verified, NMC-approved, and committed to excellence in medical education.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        {/* ---------- Performance Metrics ---------- */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {performanceMetrics.map((metric, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: alpha(metric.color, 0.2),
                  bgcolor: alpha(metric.color, 0.03),
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 40px ${alpha(metric.color, 0.15)}`,
                  },
                }}
              >
                <Box sx={{ color: metric.color, mb: 1.5 }}>{metric.icon}</Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.6rem', md: '2.2rem' },
                    color: metric.color,
                    mb: 0.5,
                  }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                >
                  {metric.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metric.sublabel}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ---------- Category Filters ---------- */}
        {categories.length > 1 && (
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Tabs
              value={category}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: 48,
                '& .MuiTabs-flexContainer': {
                  gap: { xs: 0.5, md: 1 },
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  px: { xs: 2, md: 3 },
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'none',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: alpha('#8a211d', 0.06),
                  },
                  '&.Mui-selected': {
                    color: 'white',
                    bgcolor: 'primary.main',
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat.id} value={cat.id} label={cat.label} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* ---------- College Cards Grid ---------- */}
        <TabPanel value={category} index={category}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Loading colleges...
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {displayColleges.map((college) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={college._id}>
                    <CollegeCard college={college} />
                  </Grid>
                ))}
              </Grid>

              {/* Load More Button */}
              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 5 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() =>
                      setVisibleCount((prev) => prev + LOAD_MORE_COUNT)
                    }
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: 50,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: '#8a211d',
                      borderColor: alpha('#8a211d', 0.4),
                      borderWidth: 2,
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: '#8a211d',
                        borderWidth: 2,
                        bgcolor: alpha('#8a211d', 0.06),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha('#8a211d', 0.2)}`,
                      },
                    }}
                  >
                    Load More ({remainingCount} remaining)
                  </Button>
                </Box>
              )}

              {filteredColleges.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No colleges found in this category.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* ---------- Success Highlights Section ---------- */}
        <Box
          sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: '2px solid',
            borderColor: alpha('#8a211d', 0.15),
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'relative' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    fontSize: { xs: '1.6rem', md: '2rem' },
                  }}
                >
                  Why Partner With{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    Learn Rabbit
                  </Box>
                  ?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  We are committed to providing transparent, ethical, and result-oriented admission
                  counseling. Our partnerships are built on trust, integrity, and a shared vision
                  of making quality medical education accessible to every deserving student.
                </Typography>

                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 3,
                    bgcolor: alpha('#8a211d', 0.06),
                    border: '1px solid',
                    borderColor: alpha('#8a211d', 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <TrendingUp sx={{ color: '#8a211d', fontSize: 36 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#8a211d' }}>
                      Year-over-Year Growth
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      40% increase in student placements year-on-year
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {successHighlights.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ mt: 0.3 }}>{item.icon}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {item.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* ---------- Partner University Logos ---------- */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.8rem' },
            }}
          >
            Our{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              Partner Network
            </Box>
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {partnerLogos.map((logo) => (
              <Grid item key={logo.id}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: `0 8px 25px ${alpha('#8a211d', 0.15)}`,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: alpha('#8a211d', 0.1),
                      color: '#8a211d',
                      fontWeight: 800,
                      fontSize: '1rem',
                      mb: 0.5,
                    }}
                  >
                    {logo.initials}
                  </Avatar>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textAlign: 'center',
                      px: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    {logo.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ---------- CTA Banner ---------- */}
        <Box
          sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'radial-gradient(circle at 30% 50%, rgba(138,33,29,0.2) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Ready to Start Your Medical Journey?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.1rem' },
              }}
            >
              Get free expert counselling and explore our partner colleges for your dream medical
              career. Our team is here to guide you every step of the way.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => scrollToSection('contact')}
                sx={{
                  bgcolor: '#8a211d',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: 50,
                  boxShadow: '0 8px 25px rgba(138,33,29,0.35)',
                  '&:hover': {
                    bgcolor: '#6f1a18',
                    boxShadow: '0 12px 35px rgba(138,33,29,0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s',
                }}
                endIcon={<ArrowForward />}
              >
                Apply Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => scrollToSection('contact')}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  px: 5,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 50,
                  '&:hover': {
                    borderColor: '#ffffff',
                    color: '#ffffff',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                Talk to Counselor
              </Button>
            </Stack>

            {/* Trust badges */}
            <Stack
              direction="row"
              spacing={{ xs: 2, md: 4 }}
              justifyContent="center"
              sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}
            >
              {['NMC Approved Colleges', '100% Transparency', 'Free Counselling', 'EMI Options'].map(
                (badge, i) => (
                  <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                    <CheckCircle sx={{ color: '#ffffff', fontSize: 18 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                      {badge}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PortfolioSection;